import { communityApiEndpoints, communityApiGet, communityApiPost } from '@/lib/communityApi'

export const DEFAULT_COMMUNITY_POST_PAGE_LIMIT = 20

export type CommunitySortMode = 'recent' | 'topWeek'

export type CommunityPostType = 'flash_drop' | 'new_tattoo' | 'deal' | 'general'

export type CommunityPostTypeFilter = CommunityPostType | 'all'

export type CommunityAuthorRole = 'user' | 'artist' | 'admin'

export type CommunityAudience = 'everyone' | 'artists'

export type CommunityVoteDirection = 'up' | 'down'

export type CommunityLinkedItemType = 'flash' | 'portfolioItem' | 'healedPortfolioItem'

export interface CommunityLinkedItem {
  readonly type: CommunityLinkedItemType
  readonly id: string
}

export interface CommunityPost {
  readonly id: string
  readonly boardId: string
  readonly city: string | null
  readonly country: string | null
  readonly cityDisplayLabel?: string | null
  readonly audience: CommunityAudience
  readonly userId: string
  readonly authorDisplayName?: string | null
  readonly authorRole: CommunityAuthorRole
  readonly artistId?: string | null
  readonly postType: CommunityPostType
  readonly title: string
  readonly body?: string | null
  readonly imageUrls: string[]
  readonly imageStoragePaths?: string[] | null
  readonly primarySlideIndex: number
  readonly isTattooPost: boolean
  readonly linkedItems: CommunityLinkedItem[]
  readonly portfolioItemIds: string[]
  readonly linkedPortfolioPrimaryItemId?: string | null
  readonly dealId?: string | null
  readonly upvoteCount: number
  readonly downvoteCount: number
  readonly score: number
  readonly weeklyUpvoteCount: number
  readonly weeklyDownvoteCount: number
  readonly weeklyScore: number
  readonly weeklyScoreBucket?: string | null
  readonly commentCount: number
  readonly topLevelCommentCount: number
  readonly createdAt: Date
}

interface CommunityPostApiPayload {
  readonly id: string
  readonly boardId: string
  readonly city?: string | null
  readonly country?: string | null
  readonly cityDisplayLabel?: string | null
  readonly audience?: CommunityAudience
  readonly userId: string
  readonly authorDisplayName?: string | null
  readonly authorRole: CommunityAuthorRole
  readonly artistId?: string | null
  readonly postType: CommunityPostType
  readonly title: string
  readonly body?: string | null
  readonly imageUrls: string[]
  readonly imageStoragePaths?: string[] | null
  readonly primarySlideIndex: number
  readonly isTattooPost: boolean
  readonly linkedItems?: CommunityLinkedItem[]
  readonly portfolioItemIds?: string[]
  readonly linkedPortfolioPrimaryItemId?: string | null
  readonly dealId?: string | null
  readonly upvoteCount: number
  readonly downvoteCount: number
  readonly score: number
  readonly weeklyUpvoteCount: number
  readonly weeklyDownvoteCount: number
  readonly weeklyScore: number
  readonly weeklyScoreBucket?: string | null
  readonly commentCount: number
  readonly topLevelCommentCount?: number
  readonly createdAt: string
}

function parseCommunityPost(payload: CommunityPostApiPayload): CommunityPost {
  const linkedItems = payload.linkedItems ?? []
  return {
    id: payload.id,
    boardId: payload.boardId,
    city: payload.city ?? payload.cityDisplayLabel ?? null,
    country: payload.country ?? null,
    cityDisplayLabel: payload.cityDisplayLabel ?? payload.city ?? null,
    audience: payload.audience ?? 'everyone',
    userId: payload.userId,
    authorDisplayName: payload.authorDisplayName ?? null,
    authorRole: payload.authorRole,
    artistId: payload.artistId ?? null,
    postType: payload.postType,
    title: payload.title,
    body: payload.body ?? null,
    imageUrls: payload.imageUrls ?? [],
    imageStoragePaths: payload.imageStoragePaths ?? null,
    primarySlideIndex: payload.primarySlideIndex ?? 0,
    isTattooPost: payload.isTattooPost ?? payload.postType === 'new_tattoo',
    linkedItems,
    portfolioItemIds:
      payload.portfolioItemIds ?? linkedItems.filter((item) => item.type === 'portfolioItem').map((item) => item.id),
    linkedPortfolioPrimaryItemId:
      payload.linkedPortfolioPrimaryItemId ?? (linkedItems[0]?.id ?? null),
    dealId: payload.dealId ?? null,
    upvoteCount: payload.upvoteCount ?? 0,
    downvoteCount: payload.downvoteCount ?? 0,
    score: payload.score ?? payload.upvoteCount - payload.downvoteCount,
    weeklyUpvoteCount: payload.weeklyUpvoteCount ?? 0,
    weeklyDownvoteCount: payload.weeklyDownvoteCount ?? 0,
    weeklyScore: payload.weeklyScore ?? payload.weeklyUpvoteCount - payload.weeklyDownvoteCount,
    weeklyScoreBucket: payload.weeklyScoreBucket ?? null,
    commentCount: payload.commentCount ?? 0,
    topLevelCommentCount: payload.topLevelCommentCount ?? 0,
    createdAt: new Date(payload.createdAt),
  }
}

export function resolveCommunityAuthorRole(params: {
  readonly isAdmin?: boolean
  readonly isArtist?: boolean
}): CommunityAuthorRole {
  if (params.isAdmin) return 'admin'
  if (params.isArtist) return 'artist'
  return 'user'
}

export function netVoteScoreForPost(post: CommunityPost): number {
  return post.score
}

export function netWeeklyVoteScoreForPost(post: CommunityPost): number {
  return post.weeklyScore
}

interface CommunityPostsPageResponse {
  readonly posts: CommunityPostApiPayload[]
  readonly nextCursor: string | null
}

export async function fetchCommunityPostsPage(params: {
  readonly cities: readonly string[] | null
  readonly sortMode: CommunitySortMode
  readonly postTypeFilter?: CommunityPostTypeFilter
  readonly pageLimit?: number
  readonly cursor?: string | null
}): Promise<{ posts: CommunityPost[]; nextCursor: string | null }> {
  const response = await communityApiPost<CommunityPostsPageResponse>(
    communityApiEndpoints.queryPosts,
    {
      cities: params.cities && params.cities.length > 0 ? [...params.cities] : null,
      sort: params.sortMode,
      postType: params.postTypeFilter ?? 'all',
      limit: params.pageLimit ?? DEFAULT_COMMUNITY_POST_PAGE_LIMIT,
      cursor: params.cursor ?? null,
    },
  )

  return {
    posts: response.posts.map(parseCommunityPost),
    nextCursor: response.nextCursor,
  }
}

export async function fetchCommunityPostById(postId: string): Promise<CommunityPost | null> {
  try {
    const response = await communityApiGet<CommunityPostApiPayload>(
      `${communityApiEndpoints.post}/${encodeURIComponent(postId)}`,
    )
    return parseCommunityPost(response)
  } catch {
    return null
  }
}

export async function fetchCommunityVoteDirectionsForPostsAndUser(
  postIds: readonly string[],
  userId: string,
): Promise<ReadonlyMap<string, CommunityVoteDirection>> {
  if (postIds.length === 0 || !userId) return new Map()

  const response = await communityApiPost<{ directions: Record<string, CommunityVoteDirection> }>(
    communityApiEndpoints.postVoteDirections,
    { postIds: [...postIds] },
    true,
  )

  return new Map(Object.entries(response.directions))
}

export async function createCommunityPostRecord(payload: {
  readonly city: string
  readonly country?: string | null
  readonly audience?: CommunityAudience
  readonly userId: string
  readonly authorDisplayName?: string | null
  readonly authorRole: CommunityAuthorRole
  readonly artistId?: string | null
  readonly postType: CommunityPostType
  readonly title: string
  readonly body?: string | null
  readonly imageUrls: readonly string[]
  readonly linkedItems?: readonly CommunityLinkedItem[]
  readonly dealId?: string | null
}): Promise<string> {
  const response = await communityApiPost<{ postId: string }>(
    communityApiEndpoints.post,
    {
      city: payload.city,
      country: payload.country ?? null,
      audience: payload.audience ?? 'everyone',
      authorDisplayName: payload.authorDisplayName ?? null,
      authorRole: payload.authorRole,
      artistId: payload.artistId ?? null,
      postType: payload.postType,
      title: payload.title,
      body: payload.body ?? null,
      imageUrls: [...payload.imageUrls],
      linkedItems: payload.linkedItems ? [...payload.linkedItems] : [],
      dealId: payload.dealId ?? null,
    },
    true,
  )

  return response.postId
}

export async function setCommunityPostVote(params: {
  readonly postId: string
  readonly userId: string
  readonly pressedDirection: CommunityVoteDirection
}): Promise<{ readonly resultingDirection: CommunityVoteDirection | null }> {
  return communityApiPost<{ resultingDirection: CommunityVoteDirection | null }>(
    communityApiEndpoints.postVote,
    {
      postId: params.postId,
      direction: params.pressedDirection,
    },
    true,
  )
}
